import { isAxiosError } from 'axios'
import { isHandledUnauthorizedError } from './authErrorMarker'

export type TranslateFunction = (
    key: string,
    options?: Record<string, unknown>
) => string

interface ErrorPatternMapping {
    pattern: RegExp
    key: string
    getValues?: (match: RegExpMatchArray) => Record<string, unknown>
}

const PERMISSION_LABEL_KEYS: Record<string, string> = {
    'user:read': 'errors:api.permissions.userRead',
    'user:create': 'errors:api.permissions.userCreate',
    'user:update': 'errors:api.permissions.userUpdate',
    'user:password:update': 'errors:api.permissions.userPasswordUpdate',
    'user:api-credentials:manage': 'errors:api.permissions.userApiCredentialsManage',
    'user:delete': 'errors:api.permissions.userDelete',
    'role:read': 'errors:api.permissions.roleRead',
    'rbac:role:read': 'errors:api.permissions.roleRead',
    'role:create': 'errors:api.permissions.roleCreate',
    'rbac:role:create': 'errors:api.permissions.roleCreate',
    'role:update': 'errors:api.permissions.roleUpdate',
    'rbac:role:update': 'errors:api.permissions.roleUpdate',
    'role:delete': 'errors:api.permissions.roleDelete',
    'rbac:role:delete': 'errors:api.permissions.roleDelete',
    'role:assign': 'errors:api.permissions.roleAssign',
    'rbac:role:assign': 'errors:api.permissions.roleAssign',
    'rbac:user:roles': 'errors:api.permissions.roleAssign',
    'device:distribution': 'errors:api.permissions.deviceDistribution',
    'device:distribution:read': 'errors:api.permissions.deviceDistribution',
    'statistics:species:read': 'errors:api.permissions.statistics',
    'statistics:trend:read': 'errors:api.permissions.statistics',
    'statistics:export': 'errors:api.permissions.statistics',
    'statistics:trend:export': 'errors:api.permissions.statistics',
    'home:recognition:dashboard': 'errors:api.permissions.home',
    'payment:config:create': 'errors:api.permissions.paymentConfigCreate',
    'payment:config:update': 'errors:api.permissions.paymentConfigUpdate',
    'payment:config:validate': 'errors:api.permissions.paymentConfigValidate',
    'payment:config:status:update': 'errors:api.permissions.paymentConfigStatusUpdate',
    'payment:config:set_default': 'errors:api.permissions.paymentConfigSetDefault',
    'payment:config:delete': 'errors:api.permissions.paymentConfigDelete',
}

const EXACT_ERROR_KEYS: Record<string, string> = {
    USER_ACCOUNT_INACTIVE: 'auth:login.accountInactive',
    'Username already exists': 'admin:user.messages.usernameAlreadyExists',
    'Email already exists': 'admin:user.messages.emailAlreadyExists',
    'Generated account email conflicted': 'admin:user.messages.emailAlreadyExists',
    'Invalid username format': 'admin:user.messages.invalidUsernameFormat',
    'Value error, Invalid username format': 'admin:user.messages.invalidUsernameFormat',
    '新密码不能与旧密码相同': 'errors:validation.passwordReuse',
    'User not found': 'admin:user.messages.notFound',
    'No permission to manage this user': 'admin:user.messages.noPermissionToManageUser',
    'Project owner account is bound to projects; replace the project owner before changing its role': 'admin:user.messages.boundProjectOwnerRoleChange',
    'Project owner account is bound to projects; replace the project owner before deleting the account': 'admin:user.messages.boundProjectOwnerDelete',
    'Project viewer account is authorized to projects; remove viewer project grants before changing its role': 'admin:user.messages.authorizedProjectViewerRoleChange',
    'Project viewer account is authorized to projects; remove viewer project grants before deleting the account': 'admin:user.messages.authorizedProjectViewerDelete',
    'Project users are read-only for admin users': 'admin:user.messages.projectUserReadonlyForAdmin',
    'Role not found': 'admin:role.messages.notFound',
    'Permission not found': 'admin:role.messages.permissionNotFound',
    'Role is required': 'admin:role.messages.roleRequired',
    'At least one role is required': 'admin:role.messages.atLeastOneRoleRequired',
    'System roles cannot be deleted': 'admin:role.messages.systemRoleCannotDelete',
    'System roles cannot be modified directly': 'admin:role.messages.systemRoleCannotModify',
    'Only superusers can maintain system roles': 'admin:role.messages.onlySuperusersMaintainSystemRoles',
    'Cannot create or modify a custom role named admin': 'admin:role.messages.reservedAdminRoleName',
    'Role is already assigned to users': 'admin:role.messages.roleAssignedToUsers',
    'Viewer scope already exists': 'admin:projectV1.messages.viewerScopeAlreadyExists',
    'Viewer scope not found': 'admin:projectV1.messages.viewerScopeNotFound',
    'Viewer user not found': 'admin:projectV1.messages.viewerUserNotFound',
    'User is not a project viewer': 'admin:projectV1.messages.userNotProjectViewer',
    'No viewer grant access': 'admin:projectV1.messages.noViewerGrantAccess',
    'Requested projects are out of manageable scope': 'admin:projectV1.messages.projectsOutOfScope',
    'Project not found': 'admin:project.messages.notFound',
    'Project is out of scope': 'admin:project.messages.outOfScope',
    'No device update access': 'admin:project.messages.noDeviceUpdateAccess',
    'Target project must be enabled, manageable, and outside the deletion project tree':
        'admin:project.messages.invalidReassignTarget',
    'Project device reassignment is too large for direct processing':
        'admin:project.messages.reassignmentTooLarge',
    'Owner user is invalid': 'admin:project.messages.ownerInvalid',
    'Owner user must have project owner role': 'admin:project.messages.ownerMustBeProjectManager',
    'Root project owner already manages another root project': 'admin:project.messages.rootOwnerAlreadyUsed',
    'Project owner already manages another project': 'admin:project.messages.ownerAlreadyUsed',
    'Project name already exists in this platform scope': 'admin:project.messages.nameAlreadyExists',
    'Project name already exists under the same parent project': 'admin:project.messages.nameAlreadyExists',
    'Custom region name already exists under the same parent region': 'admin:region.messages.nameAlreadyExists',
    'payment merchant not found': 'admin:paymentMerchant.feedback.notFound',
    'merchant code already exists for this channel': 'admin:paymentMerchant.feedback.duplicateMerchantCode',
    'merchant configuration must be validated before saving': 'admin:paymentMerchant.feedback.mustValidateBeforeSave',
    'merchant validation token is missing or expired': 'admin:paymentMerchant.feedback.validationTokenExpired',
    'merchant configuration changed after validation, please validate again': 'admin:paymentMerchant.feedback.validationConfigChanged',
    'unsupported payment channel': 'admin:paymentMerchant.feedback.unsupportedChannel',
    'payment merchant is already referenced by payment attempts': 'admin:paymentMerchant.feedback.referencedByAttempts',
    'payment merchant is already referenced by payment refunds': 'admin:paymentMerchant.feedback.referencedByRefunds',
    'fallback default merchant cannot be deactivated': 'admin:paymentMerchant.feedback.fallbackCannotDeactivate',
    'fallback default merchant must be active': 'admin:paymentMerchant.feedback.fallbackMustBeActive',
    'fallback default merchant must be validated successfully': 'admin:paymentMerchant.feedback.fallbackMustBeValidated',
}

const CODE_ERROR_KEYS: Record<string, string> = {
    MENDER_ARTIFACT_IN_USE: 'admin:deviceUpgrade.messages.artifactInUseOnOta',
}

const MENDER_DUPLICATE_ARTIFACT_PATTERN =
    /artifact.*(?:already exists|same name)|same name.*artifact.*already exists/i

const PATTERN_ERROR_KEYS: ErrorPatternMapping[] = [
    {
        pattern: /^Role '(.+)' already exists$/,
        key: 'admin:role.messages.roleAlreadyExists',
        getValues: (match) => ({ name: match[1] }),
    },
    {
        pattern: /^Permission '(.+)' already exists$/,
        key: 'admin:role.messages.permissionAlreadyExists',
        getValues: (match) => ({ name: match[1] }),
    },
    {
        pattern: /^Role '(.+)' is not assignable$/,
        key: 'admin:role.messages.roleNotAssignable',
        getValues: (match) => ({ name: match[1] }),
    },
    {
        pattern: /^Role (.+) not found$/,
        key: 'admin:role.messages.roleIdNotFound',
        getValues: (match) => ({ id: match[1] }),
    },
    {
        pattern: /^Project has (\d+) devices?, cannot delete$/,
        key: 'admin:project.messages.deleteBlockedByDevices',
        getValues: (match) => ({ count: Number(match[1]) }),
    },
    {
        pattern: /^Target project already has duplicate device names:/,
        key: 'admin:project.messages.duplicateDeviceNameAfterReassign',
    },
    {
        pattern: /^unsupported payment channel:/,
        key: 'admin:paymentMerchant.feedback.unsupportedChannelWithCode',
    },
]

const looksLikeEnglish = (value: string): boolean =>
    /[A-Za-z]/.test(value) && !/[\u4e00-\u9fff]/.test(value)

const extractStringFromDetail = (detail: unknown): string | null => {
    if (typeof detail === 'string') {
        return detail
    }

    if (Array.isArray(detail)) {
        const firstDetail = detail[0]
        if (typeof firstDetail === 'string') {
            return firstDetail
        }
        if (
            firstDetail &&
            typeof firstDetail === 'object' &&
            'msg' in firstDetail &&
            typeof (firstDetail as { msg?: unknown }).msg === 'string'
        ) {
            return (firstDetail as { msg: string }).msg
        }
    }

    if (detail && typeof detail === 'object') {
        const record = detail as Record<string, unknown>
        for (const key of ['message', 'detail', 'msg']) {
            const extracted = extractStringFromDetail(record[key])
            if (extracted) {
                return extracted
            }
        }
    }

    return null
}

const parsePermissionList = (value: string): string[] =>
    value
        .replace(/^\[/, '')
        .replace(/\]$/, '')
        .split(',')
        .map((item) => item.trim().replace(/^['"]|['"]$/g, ''))
        .filter(Boolean)

const translatePermissionCode = (
    permission: string,
    t: TranslateFunction
): string | null => {
    const key = PERMISSION_LABEL_KEYS[permission.trim()]
    if (!key) {
        return null
    }

    const translated = t(key)
    return translated === key ? null : translated
}

const translateMissingPermissionDetail = (
    normalized: string,
    t: TranslateFunction
): string | null => {
    const singleMatch = normalized.match(/^(?:Missing permission|缺少权限):\s*(.+)$/)
    if (singleMatch) {
        const permission = translatePermissionCode(singleMatch[1], t)
        if (!permission) {
            return t('errors:api.permissionDenied')
        }

        return t('errors:api.missingPermission', { permission })
    }

    const anyMatch = normalized.match(/^缺少(?:以下)?任一权限:\s*(.+)$/)
    if (anyMatch) {
        const permissions = parsePermissionList(anyMatch[1])
            .map((permission) => translatePermissionCode(permission, t))
            .filter((permission): permission is string => Boolean(permission))

        if (permissions.length === 0) {
            return t('errors:api.permissionDenied')
        }

        return t('errors:api.missingAnyPermission', {
            permissions: permissions.join('、'),
        })
    }

    return null
}

export const extractApiErrorDetail = (error: unknown): string | null => {
    if (!isAxiosError(error)) {
        return null
    }

    return extractStringFromDetail(error.response?.data?.detail)
}

export const extractApiErrorDetailPayload = (error: unknown): unknown => {
    if (!isAxiosError(error)) {
        return null
    }

    return error.response?.data?.detail
}

const extractApiErrorCode = (error: unknown): string | null => {
    const detail = extractApiErrorDetailPayload(error)
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) {
        return null
    }

    const code = (detail as Record<string, unknown>).code
    return typeof code === 'string' && code.trim() ? code.trim() : null
}

export const translateBackendErrorDetail = (
    detail: string | null | undefined,
    t: TranslateFunction
): string | null => {
    const normalized = detail?.trim()
    if (!normalized) {
        return null
    }

    const exactKey = EXACT_ERROR_KEYS[normalized]
    if (exactKey) {
        return t(exactKey)
    }

    const missingPermissionMessage = translateMissingPermissionDetail(normalized, t)
    if (missingPermissionMessage) {
        return missingPermissionMessage
    }

    if (MENDER_DUPLICATE_ARTIFACT_PATTERN.test(normalized)) {
        return t('admin:deviceUpgrade.messages.artifactAlreadyExistsOnOta')
    }

    for (const mapping of PATTERN_ERROR_KEYS) {
        const match = normalized.match(mapping.pattern)
        if (match) {
            return t(mapping.key, mapping.getValues?.(match))
        }
    }

    if (normalized.startsWith('Validation failed:')) {
        return t('admin:paymentMerchant.feedback.validationFailedGeneric')
    }

    if (looksLikeEnglish(normalized)) {
        console.warn('[apiErrorI18n] unmapped backend error detail', normalized)
        return null
    }

    return normalized
}

export const getLocalizedApiErrorMessage = (
    error: unknown,
    fallback: string,
    t: TranslateFunction
): string => {
    if (isHandledUnauthorizedError(error)) {
        return t('errors:auth.sessionExpired', {
            defaultValue: '登录已过期，请重新登录',
        })
    }

    const code = extractApiErrorCode(error)
    const codeKey = code ? CODE_ERROR_KEYS[code] : null
    if (codeKey) {
        return t(codeKey)
    }

    if (isAxiosError(error)) {
        const axiosMessage =
            typeof error.message === 'string' ? error.message.toLowerCase() : ''
        if (
            error.code === 'ECONNABORTED' ||
            axiosMessage.includes('timeout')
        ) {
            return t('errors:network.timeout')
        }

        if (!error.response) {
            return t('errors:network.connectionFailed')
        }
    }

    const detail = extractApiErrorDetail(error)
    const translatedDetail = translateBackendErrorDetail(detail, t)
    if (translatedDetail) {
        return translatedDetail
    }

    if (error instanceof Error && !looksLikeEnglish(error.message)) {
        return error.message
    }

    if (error instanceof Error && looksLikeEnglish(error.message)) {
        console.warn('[apiErrorI18n] unmapped error message', error.message)
    }

    return fallback
}
